// rounds/configuration — round configuration/lifecycle history
import { z } from "zod";

/**
 * One entry of `GET /intake-rounds/{id}/configuration-history/` — the append-only audit trail
 * behind the round card's "Historique de l'appel" button. `actor` is a snapshot taken at write
 * time (survives the actor's own account being later deactivated), not a live user reference, and
 * is empty for anything the system did on its own (e.g. the cron closing a round).
 *
 * `section` is the backend's own grouping (`configuration_history` in `services.py`) — the string
 * before the `:` in a `CONFIGURATION_CHANGED` audit's `reason`, or a fixed value per lifecycle
 * action for everything else. It is NOT the display "zone" the dialog groups by — see
 * `history-zones.ts` for that mapping, which is stricter and derived per CHANGED FIELD (a
 * `general` edit to `closing_at` reads as the "Dates" zone, a `general` edit to `name` doesn't).
 *
 * `before`/`after` are the raw audit payload — a full section snapshot, shape varies per
 * `section` — read only by `history-diff.ts`'s per-section diff, never rendered directly.
 */
export const RoundHistoryEntrySchema = z.object({
  id: z.string(),
  at: z.string(),
  section: z.string(),
  action: z.string(),
  operation: z.string(),
  reason: z.string(),
  before: z.unknown().nullable().default(null),
  after: z.unknown().nullable().default(null),
  actor: z
    .object({
      id: z.string().optional(),
      full_name: z.string().optional(),
      email: z.string().optional(),
    })
    .nullable(),
});

export type RoundHistoryEntry = z.infer<typeof RoundHistoryEntrySchema>;

export const RoundHistoryResponseSchema = z.object({
  round_id: z.string(),
  entries: z.array(z.unknown()),
  count: z.number().int(),
  server_now: z.string(),
});

/** `GET /configuration-history/`'s own query params (`ConfigurationHistoryQuerySerializer`). */
export interface RoundHistoryFilters {
  /** One raw `section` value — matched server-side, not one of the dialog's display zones. */
  section?: string;
  fromAt?: string;
  toAt?: string;
}

export interface RoundHistoryPage {
  entries: RoundHistoryEntry[];
  count: number;
}
